import { useFeatureFlag } from "#webapp/src/launch-darkly/useLaunchDarkly";
import { logger } from "@clockwise/client-commons/src/util/logger";
import { Button } from "@clockwise/design-system";
import { Delete } from "@clockwise/icons";
import { ResponseStatusEnum } from "@clockwise/schema";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { Skeleton } from "@material-ui/lab";
import classNames from "classnames";
import { update } from "lodash";
import React from "react";
import toast from "react-hot-toast";
import useEventResponseStatus from "../../event-scheduling/hooks/useEventResponseStatus";
import { useGatewayEventResponseStatus } from "../../event-scheduling/hooks/useGatewayEventResponseStatus";
import useEditExistingEvent from "../hooks/useEditExistingEvent";

const selectableRSVPs = [
  ResponseStatusEnum.Accepted,
  ResponseStatusEnum.Tentative,
  ResponseStatusEnum.Declined,
] as const;
type SelectableRSVP = typeof selectableRSVPs[number];

const readableResponse = (rsvp: SelectableRSVP) => {
  switch (rsvp) {
    case ResponseStatusEnum.Accepted: {
      return "Going";
    }
    case ResponseStatusEnum.Declined: {
      return "Not going";
    }
    case ResponseStatusEnum.Tentative: {
      return "Maybe";
    }
  }
};

const RSVPButtons = ({
  externalEventId,
  calendarId,
  responseState,
  onClose,
  truncatedTitle,
}: {
  externalEventId: string;
  calendarId: string;
  responseState: ResponseStatusEnum;
  onClose: () => void;
  truncatedTitle: string;
}) => {
  const [isUsingGateway] = useFeatureFlag("GatewayCalendar");
  const { update: updateGateway, updating: updatingGateway } = useGatewayEventResponseStatus(
    externalEventId,
    calendarId,
  );
  const { update: updateWebserver, updating: updatingWebserver } = useEventResponseStatus(
    externalEventId,
    calendarId,
  );
  const updating = updatingGateway || updatingWebserver;
  const track = useTracking();

  const handleSelectRSVP = isUsingGateway
    ? (value: SelectableRSVP) => {
        if (value === responseState) return;
        if (!update) {
          toast.error("Failed to update meeting response");
          onClose();
          return;
        }
        track(TrackingEvents.EVENT_POPOVER.RSVP_SELECTED, {
          rsvp: value,
        });
        void updateGateway({
          responseStatus: value,
          onCompleted: () => {
            toast.success(`Updated ${truncatedTitle} to ${readableResponse(value)}`);
          },
          onError: () => {
            toast.error("Failed to update meeting response");
          },
        });
      }
    : (value: SelectableRSVP) => {
        if (value === responseState) return;
        if (!updateWebserver) {
          toast.error("Failed to update meeting response");
          onClose();
          return;
        }
        track(TrackingEvents.EVENT_POPOVER.RSVP_SELECTED, {
          rsvp: value,
        });
        void updateWebserver({
          variables: { responseStatus: value },
          onCompleted: () => {
            toast.success(`Updated ${truncatedTitle} to ${readableResponse(value)}`);
          },
          onError: () => {
            toast.error("Failed to update meeting response");
          },
        });
      };

  return (
    <div className="cw-flex">
      <Button
        sentiment={responseState === ResponseStatusEnum.Accepted ? "positive" : "neutral"}
        variant="outlined"
        size="mini"
        disabled={updating}
        onClick={() => {
          handleSelectRSVP(ResponseStatusEnum.Accepted);
          onClose();
        }}
      >
        Yes
      </Button>

      <div className="cw-mx-1">
        <Button
          sentiment={responseState === ResponseStatusEnum.Declined ? "positive" : "neutral"}
          variant="outlined"
          size="mini"
          disabled={updating}
          onClick={() => {
            handleSelectRSVP(ResponseStatusEnum.Declined);
            onClose();
          }}
        >
          No
        </Button>
      </div>
      <Button
        sentiment={responseState === ResponseStatusEnum.Tentative ? "positive" : "neutral"}
        variant="outlined"
        size="mini"
        disabled={updating}
        onClick={() => {
          handleSelectRSVP(ResponseStatusEnum.Tentative);
          onClose();
        }}
      >
        Maybe
      </Button>
    </div>
  );
};

const DeleteEvent = ({
  externalEventId,
  calendarId,
  canDelete,
  onClose,
}: {
  externalEventId: string;
  calendarId: string;
  canDelete: boolean;
  onClose: () => void;
}) => {
  const { onDeleteEvent, deletingEvent } = useEditExistingEvent(
    externalEventId,
    calendarId,
    false,
    {
      refetchQueriesOnDelete: ["CalendarEvents"],
      onDeletionCompleted: () => {
        toast.success(canDelete ? "Event deleted." : "Event removed.");
        onClose();
      },
      onDeletionError: (err) => {
        toast.error("Failed to update event.");
        logger.error("unabled to delete event from event popover", {
          error: err || "unabled to delete event from event popove",
          externalEventId,
          calendarId,
        });
      },
    },
  );

  const onClick = () => {
    void onDeleteEvent();
  };

  return (
    <Button
      sentiment="neutral"
      variant="outlined"
      size="mini"
      startIcon={Delete}
      disabled={deletingEvent}
      onClick={onClick}
    />
  );
};

export const Footer = ({
  canRSVP,
  canDelete,
  canRemove,
  responseState,
  onClose,
  truncatedTitle,
  externalEventId,
  calendarId,
  showEventOwner,
  ownerDisplayName,
  loading,
}: {
  canRSVP: boolean;
  canDelete: boolean;
  canRemove: boolean;
  responseState: ResponseStatusEnum;
  onClose: () => void;
  truncatedTitle: string;
  externalEventId: string;
  calendarId: string;
  showEventOwner: boolean;
  ownerDisplayName: string;
  loading: boolean;
}) => {
  return (
    <div
      className={classNames(
        "cw-flex cw-rounded-b-xl cw-p-2 cw-bg-neutral cw-w-full cw-border-t cw-border-solid cw-border-muted",
        {
          "cw-justify-between": canRSVP,
          "cw-justify-end": !canRSVP && canDelete,
        },
      )}
    >
      {canRSVP && (
        <RSVPButtons
          externalEventId={externalEventId}
          responseState={responseState}
          onClose={onClose}
          truncatedTitle={truncatedTitle}
          calendarId={calendarId}
        />
      )}
      {(canDelete || canRemove) && (
        <DeleteEvent
          externalEventId={externalEventId}
          calendarId={calendarId}
          canDelete={canDelete}
          onClose={onClose}
        />
      )}
      {showEventOwner &&
        !canRSVP &&
        !canDelete &&
        (loading ? (
          <Skeleton variant="rect" width={150} height={18} />
        ) : (
          <div className="cw-text-muted cw-body-small cw-text-12">
            Owner: <span className="cw-font-medium">{ownerDisplayName}</span>
          </div>
        ))}
    </div>
  );
};
